/**
 * The Agent runs panel's changes (#169 slice 3). Each refreshes what it
 * changed, which is how the timer-less panel stays current:
 *  - pause answers with the fresh panel and refetches the
 *    project's definitions. The answer is stored as is (no second GitLab
 *    scan) only when it is surely the newest: no other panel write in flight
 *    and none settled, and no panel GET started, since the PUT was sent
 *    (`panelEpoch`). A GET still in flight then started earlier and may have
 *    read the settings before the commit, so it is cancelled. Otherwise the
 *    panel refetches;
 *  - Save and Withdraw invalidate the panel and every definition of the
 *    project (they act by name, and a name's file may be `.yaml` or `.yml`).
 *    So does a Withdraw refused with 409 (no Sanctum MR, or now another
 *    one than the viewer confirmed): the pane then shows the MR now open,
 *    and the refusal stays with the project page (`onWithdrawOutcome`),
 *    which the refetch doesn't remount. And so does a Save refused with
 *    409 (the text changed since the editor opened, or the branch is
 *    another's): the detail, unmounted under the editor, refetches when
 *    the editor closes, and its Edit waits for that (`DefinitionActions`),
 *    so Edit again starts from the newer text, as "reload it" says.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { saveDefinition, setPaused, withdrawDefinition } from '../api/projectPanel';
import { refreshDefinitions, storePanel, writeOptions, type WriteContext } from './panelWrites';
import type { PanelResponse, SaveResponse, WithdrawResponse } from '../types/projectPanel.types';

export interface SaveDefinitionInput {
  name: string;
  content: string;
  /** The text the editor started from; null for a new definition. */
  baseContent: string | null;
}

type PanelWrite = UseMutationResult<PanelResponse, Error, boolean, WriteContext>;

export function useSetPaused(projectId: string): PanelWrite {
  const client = useQueryClient();
  return useMutation({
    ...writeOptions(client, projectId),
    mutationFn: (paused: boolean) => setPaused(projectId, paused),
    onSuccess: (panel, _paused, sent) => storePanel(client, projectId, panel, sent),
  });
}

export function useSaveDefinition(
  projectId: string,
): UseMutationResult<SaveResponse, Error, SaveDefinitionInput, WriteContext> {
  const client = useQueryClient();
  return useMutation({
    ...writeOptions(client, projectId),
    mutationFn: ({ name, content, baseContent }: SaveDefinitionInput) =>
      saveDefinition(projectId, name, content, baseContent),
    onSuccess: () => {
      refreshDefinitions(client, projectId);
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) refreshDefinitions(client, projectId);
    },
  });
}

/** The definition, and the Sanctum MR the viewer confirmed closing. */
export interface WithdrawDefinitionInput {
  name: string;
  iid: number;
}

export function useWithdrawDefinition(
  projectId: string,
): UseMutationResult<WithdrawResponse, Error, WithdrawDefinitionInput, WriteContext> {
  const client = useQueryClient();
  return useMutation({
    ...writeOptions(client, projectId),
    mutationFn: ({ name, iid }: WithdrawDefinitionInput) => withdrawDefinition(projectId, name, iid),
    onSuccess: () => {
      refreshDefinitions(client, projectId);
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) refreshDefinitions(client, projectId);
    },
  });
}
